import React, {useCallback} from 'react';
import {generateSectionId} from '../../pages/network-optimization/pages/generate-report/generateTableOfContents';
import './toc.scss';

export interface ITOCItem {
    title: string;
    page?: string | number;
    children?: ITOCItem[];
}
export type ITOCProps = {
    items: Array<ITOCItem>;
    className?: string;
    title?: string;
    showPageNumbers?: boolean;
    indentSize?: number;
    getId?: (item: ITOCItem) => string;
    decorator?: React.ReactNode;
    getItemClassname?: (level: number) => string;
}

const TableOfContents = React.forwardRef((props: ITOCProps, ref: React.ForwardedRef<HTMLDivElement>) => {

    const {
        items = [],
        className = 'table-of-contents',
        title = 'Table of Contents',
        showPageNumbers = false,
        getId = ({title}: {title: string}) => generateSectionId(title),
        decorator,
        indentSize = 24,
        getItemClassname = (level: number) => level === 0 ? 'toc-item' : 'toc-subitem',
    } = props;

    const renderTOCItem = useCallback((item: ITOCItem, level = 0) => {
        const id = getId(item);
        const children = item.children || [];
        const className = getItemClassname(level);
        return (
            <React.Fragment key={id}>
                <li
                    style={{
                        marginLeft: `${level * indentSize}px`
                    }}
                    className={className}
                >
                    <a href={`#${id}`}>{item.title}</a>
                    {showPageNumbers && (
                        <>
                            {decorator || <span className='dots'><span /></span>}
                            <span className='page-number'>{item.page}</span>
                        </>
                    )}
                </li>
                {children.length > 0 && children.map((child) => renderTOCItem(child, level + 1))}
            </React.Fragment>
        );
    }, [decorator, getId, getItemClassname, indentSize, showPageNumbers]);

    return (
        <nav className={className} ref={ref}>
            <h2>{title}</h2>
            <ul>
                {items.map((item) => renderTOCItem(item))}
            </ul>
        </nav>
    );
});

TableOfContents.displayName = 'TableOfContents';

export default TableOfContents;
